import { Component, computed, Pipe, PipeTransform, signal, viewChild, WritableSignal, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { WorkGridItemObject } from '@it2go/types';
import { formSetEnumOptions } from '../../../../../../../../types/src/services/enum/form-set.enum';
import moment from 'moment';
import { WorkVisitsPageStore } from './work-visits-page.store';
import { WorkTypeEnum } from '@libs/types/src/common/enum/work-type.enum';
import { workUri } from 'packages/client/src/app/utils/router.utils';
import { insuranceAbbrs } from '@libs/types/src/common/enum/insurance-abbr.enum';
import { TranslatePipe } from '@ngx-translate/core';
import { ConfirmPopup } from '@openng/optimus-ui/confirmpopup';
import {
    RealizedWorkPageHeaderComponent,
} from '../../../../../modules/content/pages/services/realized-work-pages/realized-work-page-header/realized-work-page-header.component';
import { TableComponent } from '../../../../../modules/shared/components/kit/table/table.component';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { WorkSumPipe } from '../../../../../modules/content/components/pipe/work/work-sum.pipe';
import {
    WorkUniqueDaysPipe,
    WorkUniquesPipe,
} from '../../../../../modules/content/components/pipe/work/work-uniques.pipe';
import {
    FolderFormDataWorkColumnComponent,
} from '../../../../../modules/content/components/services/forms/folder-form-data-work-column/folder-form-data-work-column.component';
import { SubscriptionComponent } from '../../../../../modules/shared/helpers/subscription.component';
import { TimePeriodEnum } from '@libs/types/src/common/enum/timePeriod.enum';
import { TableColumn, TableColumnType } from '../../../../../modules/shared/components/kit/table/table.types';
import {
    boolColumn,
    dateFromColumnWithTime,
    dateToColumnWithTime,
    dayDateFromColumn,
    filesColumn,
    folderPersonColumn,
    performedProceduresColumn,
    signatureField,
    usersChipColumn,
} from '../../../../../modules/shared/components/kit/table/table.fields';
import { localDate } from '../../../../../modules/shared/pipes/local-date.pipe';
import { SessionService } from '../../../../../services/session.service';
import { ConfirmBuilder } from '../../../../../modules/shared/service/confirm.builder';
import { contentGlobalSuccess } from '../../../../../modules/content/content.error';
import { workHideDelete } from '../../work-detail/utils/work.transform-utils';
import { ButtonComponent } from '../../../../kit/button/button.component';
import { WorkPrintService } from '../../work-print.service';
import { featureOn } from '../../../../../feature-flag';
import { getWorksFrag } from './work-visits-page.query';

@Pipe({
    name: 'workLength',
})
export class WorkLengthPipe implements PipeTransform {

    public transform(value: WorkGridItemObject, ...args: any[]): string {
        return getWorkLength(value.dateFrom, value.dateTo);
    }
}

export function getWorkLength(visitFrom: string, visitTo: string | null): string {
    if (!visitFrom || !visitTo) return '0';

    const visitFromDate = new Date(visitFrom);
    const visitToDate = new Date(visitTo);

    const visitFromHours = visitFromDate.getHours();
    const visitFromMinutes = visitFromDate.getMinutes();
    const visitToHours = visitToDate.getHours();
    const visitToMinutes = visitToDate.getMinutes();

    const visitFromMs = visitFromHours * 60 * 60 * 1000 + visitFromMinutes * 60 * 1000;
    const visitToMs = visitToHours * 60 * 60 * 1000 + visitToMinutes * 60 * 1000;

    const diffMs = Math.abs(visitToMs - visitFromMs);
    return Math.floor(diffMs / (1000 * 60)).toString();
}

@Component({
    selector: 'app-work-visits-page',
    templateUrl: 'work-visits-page.component.html',
    styleUrls: ['work-visits-page.component.scss'],
    providers: [WorkVisitsPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TranslatePipe,
        ConfirmPopup,
        RealizedWorkPageHeaderComponent,
        TableComponent,
        PrimeTemplate,
        WorkSumPipe,
        WorkUniqueDaysPipe,
        WorkUniquesPipe,
        FolderFormDataWorkColumnComponent,
        ButtonComponent,
    ],
})
export class WorkVisitsPageComponent extends SubscriptionComponent {

    protected readonly store = inject(WorkVisitsPageStore);
    private readonly fb = inject(FormBuilder);
    private readonly router = inject(Router);
    private readonly session = inject(SessionService);
    private readonly confirm = inject(ConfirmBuilder);
    protected readonly workPrint = inject(WorkPrintService);
    protected readonly extendedWorkExportsEnabled = featureOn.EXTENDED_WORK_EXPORTS;

    protected table = viewChild(TableComponent);

    protected form = this.fb.group({
        date: this.fb.control(moment().startOf('d').toDate()),
        endDate: this.fb.control<Date>(new Date()),
        selectedVisits: this.fb.control<string>('all'),
        selectedPeriod: this.fb.control<TimePeriodEnum>(TimePeriodEnum.Day, {
            nonNullable: true,
        }),
    });

    protected dateFilter: WritableSignal<any> = signal([]);
    protected workplaceFilter: WritableSignal<any> = signal([]);
    protected extraFilters = computed(() => [
        [
            {
                value: [this.store.globalGlobalStore.organizationStore.selectedId()],
                column: 'organizationId',
                operator: 'EQ',
            },
        ],
        [
            {
                value: [WorkTypeEnum.VISIT],
                column: 'workType',
                operator: 'EQ',
            },
        ],
        ...this.dateFilter(),
        ...this.workplaceFilter(),
    ]);

    protected columns: TableColumn[] = [
        folderPersonColumn,
        signatureField('signature'),
        dayDateFromColumn,
        dateFromColumnWithTime,
        dateToColumnWithTime,
        {
            name: 'workData.wdv.addressFrom',
            dataKey: 'workData.addressFrom',
            filterable: true,
            type: TableColumnType.MapLink,
        },
        {
            name: 'workData.wdv.zipFrom',
            dataKey: 'workData.addressFrom',
            filterable: true,
            data: (it: any) => it?.zip,
        },
        {
            name: 'workData.wdv.addressTo',
            dataKey: 'workData.addressTo',
            filterable: true,
            type: TableColumnType.MapLink,
        },
        {
            name: 'workData.wdv.zipTo',
            dataKey: 'workData.addressTo',
            filterable: true,
            data: (it: any) => it?.zip,
        },
        {
            name: 'workData.wdv.distanceFrom',
            dataKey: 'workData.distanceFrom',
            filterable: {
                type: 'number',
            },
        },
        {
            name: 'workData.wdv.distanceTo',
            dataKey: 'workData.distanceTo',
            filterable: {
                type: 'number',
            },
        },
        usersChipColumn,
        performedProceduresColumn,
        {
            name: 'folderFormData',
            filterable: {
                type: 'select',
                selectOptions: () => formSetEnumOptions,
            },
            exportAs: (folderFormData: any): string => {
                const counts: Record<string, number> = {};
                folderFormData?.forEach((it: any) => {
                    const uid = it.folderForm.form.uniqueIdentifier;
                    counts[uid] = (counts[uid] || 0) + 1;
                });

                return Object.entries(counts).map(([code, count]) => `${count}x ${code}`).join(', ');
            },
        },
        {
            name: 'voucherNumber',
            filterable: {
                type: 'number',
            },
        },
        {
            name: 'insuranceAbbr',
            dataKey: 'person.insurances.0.insuranceAbbr',
            filterable: {
                type: 'select',
                selectOptions: () => insuranceAbbrs,
            },
        },
        {
            name: 'workday',
            dataKey: 'dateFrom',
            data: (it: any) => localDate(it, this.session, 'workday'),
            type: TableColumnType.Bool,
            filterable: {
                type: 'select',
                selectOptions: () => [
                    { name: 'all', value: 'all' },
                    { name: 'yes', value: '1' },
                    { name: 'no', value: '0' },
                ],
            },
        },
        boolColumn('workData.wdv.locked', true, 'workData.locked'),
        filesColumn,
    ];

    protected contextMenuItems = [
        // temporally commented - must be finished
        {
            label: 'table.signSelected',
            icon: 'pi pi-shield',
            command: () => console.log('sign action', this.table()?.selection(), this.table()?.selectedContextRow),
            visible: true,
        },
    ];

    constructor() {
        super();

        this.store.onSuccess(
            () => this.table()?.reload(),
            'updateWork',
        );

        this.subs.push(
            this.form.valueChanges.subscribe((value) => {
                if (value.date && value.endDate) {
                    this.dateFilter.set([[
                        {
                            value: [
                                value.date.toISOString(),
                                value.endDate.toISOString(),
                            ],
                            column: 'dateFrom',
                            operator: 'BETWEEN',
                        },
                    ]]);
                } else {
                    this.dateFilter.set([]);
                }
                switch (value.selectedVisits) {
                    case 'all':
                        this.workplaceFilter.set([]);
                        break;
                    case 'mine':
                        this.workplaceFilter.set([[
                            {
                                column: 'userId',
                                operator: 'EQ',
                                value: [this.store.globalGlobalStore.loggedUserStore.currentUserId()],
                            },
                        ]]);
                        break;
                    case 'mineWorkplace':
                        const workplaceFilter = this.store.globalGlobalStore.loggedUserStore.filterByWorkplaceIdOrServiceOfNonAssigned()();
                        this.workplaceFilter.set(workplaceFilter);
                        break;
                }
            }),
        );
    }

    protected async detail(item: WorkGridItemObject): Promise<void> {
        await this.router.navigateByUrl(workUri(item, item.serviceId));
    }

    protected delete(input: any): void {
        this.confirm.confirm(
            'common.delete',
            null,
            async () => {
                await this.store.api.deleteWork({ id: input.item.id });
                this.table()?.reload();
                contentGlobalSuccess.next('common.deleted');
            },
            null,
            'pi-trash',
            input.event.target,
        );
    }

    protected readonly workHideDelete = workHideDelete(this.store.globalGlobalStore);

    protected printRecords(): void {
        this.workPrint.openBulk({
            selection: this.table()?.selection(),
            supportedWorkTypes: [WorkTypeEnum.VISIT],
            dateFrom: this.form.controls.date.value,
            dateTo: this.form.controls.endDate.value,
        });
    }

    protected printRecord(item: WorkGridItemObject): void {
        this.workPrint.openRow(item);
    }

    protected readonly getWorksFrag = getWorksFrag;
}
